import { type RefObject, useLayoutEffect, useState } from 'react';

import { computeMaxVisibleTabsCount, TABS_HORIZONTAL_GAP_PX } from './computeMaxVisibleTabsCount';

type UseFitTabsToContainerParams = {
  containerRef: RefObject<HTMLElement | null>;
  enabled: boolean;
  measureRef: RefObject<HTMLElement | null>;
  seeMoreMeasureRef: RefObject<HTMLElement | null>;
};

/**
 * Measures the tabs container and returns how many tabs fit before "See more".
 * @param params.containerRef Ref of the visible tabs container.
 * @param params.enabled Whether width-based fitting is active.
 * @param params.measureRef Ref of the hidden row used to measure each tab width.
 * @param params.seeMoreMeasureRef Ref of the hidden "See more" control used for width reservation.
 * @returns The fitted visible tabs count, or `null` while measuring is disabled / not ready.
 */
export const useFitTabsToContainer = ({
  containerRef,
  enabled,
  measureRef,
  seeMoreMeasureRef,
}: UseFitTabsToContainerParams): number | null => {
  const [fittedCount, setFittedCount] = useState<number | null>(null);

  useLayoutEffect(() => {
    if (!enabled) {
      setFittedCount(null);
      return undefined;
    }

    const containerElement = containerRef.current;
    const measureElement = measureRef.current;
    const seeMoreElement = seeMoreMeasureRef.current;

    if (!containerElement || !measureElement || !seeMoreElement) {
      return undefined;
    }

    const updateFittedCount = (): void => {
      const tabWidths = Array.from(
        measureElement.children,
        (child) => (child as HTMLElement).getBoundingClientRect().width,
      );
      const nextFittedCount = computeMaxVisibleTabsCount({
        containerWidth: containerElement.getBoundingClientRect().width,
        gap: TABS_HORIZONTAL_GAP_PX,
        seeMoreWidth: seeMoreElement.getBoundingClientRect().width,
        tabWidths,
      });

      setFittedCount((previous) => (previous === nextFittedCount ? previous : nextFittedCount));
    };

    updateFittedCount();

    const resizeObserver = new ResizeObserver(updateFittedCount);
    resizeObserver.observe(containerElement);
    resizeObserver.observe(measureElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, [containerRef, enabled, measureRef, seeMoreMeasureRef]);

  return fittedCount;
};
